import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { formatDateTime } from '@/utils/date'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AiAllEnvSection } from './AiAllEnvSection'
import { AiSpendSection } from './AiSpendSection'
import { AiOwnersSection } from './AiOwnersSection'
import { AiRegisterSection } from './AiRegisterSection'
import { AiRiskActionsSection } from './AiRiskActionsSection'
import { AiRiskReportSection } from './AiRiskReportSection'
import { AiIncidentsSection } from './AiIncidentsSection'
import { AiControlsSection } from './AiControlsSection'
import { AiFirmLimitsSection } from './AiFirmLimitsSection'

// MONITOR-THIN — super-admin-only operator view. Read-only. Statuses/metadata
// ONLY (privacy wall — never document content). Full console = ADMIN-MONITOR-1.

interface AuditEvent {
  event_id: string
  timestamp: string | null
  actor: string | null
  actor_name: string | null
  actor_role: string | null
  action: string | null
  target: string | null
  engagement_id: string | null
  before_state: string | null
  after_state: string | null
  ip_address: string | null
  user_agent: string | null
}

interface AuditLogResponse {
  total_count: number
  limit: number
  offset: number
  events: AuditEvent[]
}

interface HealthState {
  db: string | null
  version: string | null
  reachable: boolean
}

function fmtDateTime(iso: string | null) {
  if (!iso) return '—'
  return formatDateTime(iso)
}

function roleBadge(role: string | null) {
  if (!role) return <span className="text-gray-400">—</span>
  const map: Record<string, string> = {
    super_admin: 'bg-purple-100 text-purple-800',
    cpa: 'bg-blue-100 text-blue-800',
    tester: 'bg-amber-100 text-amber-800',
    system: 'bg-gray-100 text-gray-700',
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[role] ?? 'bg-gray-100 text-gray-700'}`}
    >
      {role}
    </span>
  )
}

export function MonitorPage() {
  const { data, loading, error, execute } = useApi<AuditLogResponse>()
  const [health, setHealth] = useState<HealthState>({ db: null, version: null, reachable: true })

  const loadEvents = useCallback(() => {
    execute(() =>
      apiClient
        .get<AuditLogResponse>('/v1/admin/audit-log', { params: { limit: 200 } })
        .then(r => r.data),
    )
  }, [execute])

  const loadHealth = useCallback(async () => {
    const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
    try {
      const [hz, ver] = await Promise.all([
        fetch(`${base}/healthz`, { credentials: 'include' }).then(r => r.json()),
        apiClient.get<{ version: string }>('/v1/version').then(r => r.data),
      ])
      setHealth({ db: hz?.db ?? null, version: ver?.version ?? null, reachable: true })
    } catch {
      setHealth({ db: null, version: null, reachable: false })
    }
  }, [])

  const refresh = useCallback(() => {
    loadEvents()
    loadHealth()
  }, [loadEvents, loadHealth])

  useEffect(() => {
    refresh()
  }, [refresh])

  const events = data?.events ?? []
  const errors = events.filter(e => e.action === 'error')
  const activity = events.filter(e => e.action !== 'error')

  const dbOk = health.db === 'azure_sql'

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Operator Monitor</h1>
          <p className="text-sm text-gray-500 mt-1">
            Read-only activity + error feed. Metadata only — no document content.
          </p>
        </div>
        <Button variant="outline" onClick={refresh}>Refresh</Button>
      </div>

      {/* Health strip */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-gray-50 px-4 py-3 text-sm">
        <span className="font-medium text-gray-700">Health</span>
        <span className="text-gray-300">|</span>
        <span className="text-gray-600">
          version <span className="font-mono">{health.version ?? '—'}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1.5">
          db
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              !health.reachable
                ? 'bg-red-100 text-red-800'
                : dbOk
                  ? 'bg-green-100 text-green-800'
                  : 'bg-amber-100 text-amber-800'
            }`}
          >
            {!health.reachable ? 'unreachable' : health.db ?? 'unknown'}
          </span>
        </span>
      </div>

      {error && <div className="text-sm text-red-600">Failed to load audit log.</div>}

      {/* Recent Errors */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Recent Errors <span className="text-sm font-normal text-gray-500">({errors.length})</span>
        </h2>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : errors.length === 0 ? (
          <p className="text-sm text-gray-500">No errors captured.</p>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Time</TableHead>
                  <TableHead>Path</TableHead>
                  <TableHead>Code / Correlation</TableHead>
                  <TableHead>Source IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {errors.map(e => (
                  <TableRow key={e.event_id}>
                    <TableCell className="text-sm whitespace-nowrap">{fmtDateTime(e.timestamp)}</TableCell>
                    <TableCell className="text-sm font-mono">{e.target ?? '—'}</TableCell>
                    <TableCell className="text-sm font-mono text-red-700">{e.after_state ?? '—'}</TableCell>
                    <TableCell className="text-sm text-gray-500">{e.ip_address ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <AiOwnersSection />
      <AiRegisterSection />
      <AiRiskActionsSection />
      <AiRiskReportSection />
      <AiIncidentsSection />
      <AiControlsSection />
      <AiFirmLimitsSection />
      <AiAllEnvSection />
      <AiSpendSection />

      {/* Recent Activity */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Recent Activity <span className="text-sm font-normal text-gray-500">({activity.length})</span>
        </h2>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : activity.length === 0 ? (
          <p className="text-sm text-gray-500">No activity yet.</p>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Time</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activity.map(e => (
                  <TableRow key={e.event_id}>
                    <TableCell className="text-sm whitespace-nowrap">{fmtDateTime(e.timestamp)}</TableCell>
                    <TableCell className="text-sm">{e.actor_name || e.actor || '—'}</TableCell>
                    <TableCell>{roleBadge(e.actor_role)}</TableCell>
                    <TableCell className="text-sm font-medium">{e.action ?? '—'}</TableCell>
                    <TableCell className="text-sm text-gray-600 font-mono">{e.target ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  )
}
